/**
 * AdminTextBlocks — Phase 3 Rich-Text-Editor (Iter89z-rtb, 2026-02-26).
 *
 * Admin pflegt frei definierbare Textbaustein-Vorlagen (Grußformeln,
 * Zahlungshinweise, Signatur-Footer etc.) mit Tiptap-Rich-Text-Editor
 * und `${…}` Placeholder-Chips. Preview löst Platzhalter gegen
 * Beispiel-Kontext auf, "Als PDF testen" erzeugt eine PDF-Vorschau.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus, Loader2, Search, Save, Trash2, Eye, FileText, Edit3, X, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import TextBlockEditor from "@/components/text_block/TextBlockEditor";

const CATEGORIES = [
  { key: "angebot",   label: "Angebote" },
  { key: "vertrag",   label: "Verträge" },
  { key: "rechnung",  label: "Rechnungen" },
  { key: "protokoll", label: "Protokolle" },
  { key: "uebergabe", label: "Übergabe" },
  { key: "kunde",     label: "Kunden-Komm." },
  { key: "sonstige",  label: "Sonstige" },
];

const CATEGORY_COLORS = {
  angebot:   "bg-emerald-500/10 border-emerald-500/40 text-emerald-200",
  vertrag:   "bg-sky-500/10 border-sky-500/40 text-sky-200",
  rechnung:  "bg-amber-500/10 border-amber-500/40 text-amber-200",
  protokoll: "bg-violet-500/10 border-violet-500/40 text-violet-200",
  uebergabe: "bg-rose-500/10 border-rose-500/40 text-rose-200",
  kunde:     "bg-cyan-500/10 border-cyan-500/40 text-cyan-200",
  sonstige:  "bg-zinc-500/10 border-zinc-500/40 text-zinc-300",
};

const EMPTY_BLOCK = {
  name: "",
  description: "",
  category: "sonstige",
  html: "",
  is_active: true,
};

export default function AdminTextBlocks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [editing, setEditing] = useState(null); // null | 'new' | <id>
  const [draft, setDraft] = useState(EMPTY_BLOCK);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/text-blocks");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Textbausteine konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = items;
    if (catFilter) out = out.filter((t) => t.category === catFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((t) =>
        (t.name || "").toLowerCase().includes(s) ||
        (t.description || "").toLowerCase().includes(s)
      );
    }
    return out;
  }, [items, q, catFilter]);

  const openNew = () => {
    setDraft(EMPTY_BLOCK);
    setPreviewHtml("");
    setEditing("new");
  };

  const openEdit = (tpl) => {
    setDraft({
      name: tpl.name || "",
      description: tpl.description || "",
      category: tpl.category || "sonstige",
      html: tpl.html || "",
      is_active: tpl.is_active !== false,
    });
    setPreviewHtml("");
    setEditing(tpl.id);
  };

  const closeEdit = () => {
    setEditing(null);
    setDraft(EMPTY_BLOCK);
    setPreviewHtml("");
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Name darf nicht leer sein");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        const r = await api.post("/text-blocks", draft);
        setItems((prev) => [...prev, r.data.text_block]);
        toast.success("Textbaustein angelegt");
      } else {
        const r = await api.patch(`/text-blocks/${editing}`, draft);
        setItems((prev) => prev.map((t) => (t.id === editing ? r.data.text_block : t)));
        toast.success(`Textbaustein aktualisiert · v${r.data.text_block.version}`);
      }
      closeEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Textbaustein „${name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/text-blocks/${id}`);
      setItems((prev) => prev.filter((t) => t.id !== id));
      toast.success("Textbaustein gelöscht");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/text-blocks/preview", { html: draft.html });
      setPreviewHtml(r.data.resolved_html || "<em>(leer)</em>");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview fehlgeschlagen");
    } finally {
      setPreviewing(false);
    }
  };

  const downloadPdf = async () => {
    if (!editing || editing === "new") {
      toast.info("Bitte erst speichern — dann kann die PDF-Preview erzeugt werden.");
      return;
    }
    try {
      const r = await api.post(`/text-blocks/${editing}/render-pdf`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error("PDF-Preview fehlgeschlagen");
    }
  };

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const k = t.category || "sonstige";
      if (!map[k]) map[k] = [];
      map[k].push(t);
    });
    return map;
  }, [filtered]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-100 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#00E575]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 rounded-full px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-wider font-bold mb-2">
              <FileText size={11} /> Iter89z-rtb · Phase 3
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight">Textbausteine</h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Freie Vorlagen für Grußformeln, Zahlungshinweise, Fußzeilen etc. — mit Rich-Text-Editor und Platzhalter-Chips.
              Platzhalter werden beim Rendern gegen Kunden-/Projektdaten ausgetauscht.
            </p>
          </div>
          <button
            onClick={openNew}
            data-testid="textblocks-new-btn"
            className="h-10 px-4 rounded-lg bg-[#00E575] text-[#09090B] hover:bg-[#00C964] text-[13px] font-bold inline-flex items-center gap-1.5"
          >
            <Plus size={14} /> Neuer Textbaustein
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche nach Name oder Beschreibung …"
              data-testid="textblocks-search"
              className="w-full h-10 pl-9 pr-3 bg-[#18181B] border border-zinc-800 rounded-lg text-[13px] focus:border-[#00E575] outline-none"
            />
          </div>
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            data-testid="textblocks-cat-filter"
            className="h-10 px-3 bg-[#18181B] border border-zinc-800 rounded-lg text-[13px]"
          >
            <option value="">Alle Kategorien</option>
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>

        {items.length === 0 && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-6 text-center">
            <AlertCircle className="mx-auto mb-2 text-amber-400" size={28} />
            <p className="text-amber-200 font-semibold">Noch keine Textbausteine angelegt.</p>
            <p className="text-zinc-400 text-sm mt-1">
              Legen Sie z.B. eine Grußformel, einen Zahlungshinweis oder eine Signatur an.
            </p>
          </div>
        )}

        {CATEGORIES.map((cat) => {
          const list = grouped[cat.key];
          if (!list?.length) return null;
          return (
            <section key={cat.key} className="mb-8" data-testid={`textblocks-cat-${cat.key}`}>
              <h2 className={`font-display font-bold text-sm uppercase tracking-wider mb-3 inline-block px-3 py-1 rounded-lg border ${CATEGORY_COLORS[cat.key]}`}>
                {cat.label} <span className="opacity-60">· {list.length}</span>
              </h2>
              <div className="grid gap-3">
                {list.map((tpl) => (
                  <BlockCard
                    key={tpl.id}
                    tpl={tpl}
                    onEdit={() => openEdit(tpl)}
                    onDelete={() => remove(tpl.id, tpl.name)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {editing && (
        <EditorModal
          mode={editing === "new" ? "new" : "edit"}
          draft={draft}
          setDraft={setDraft}
          onClose={closeEdit}
          onSave={save}
          onPreview={preview}
          onPdf={downloadPdf}
          saving={saving}
          previewing={previewing}
          previewHtml={previewHtml}
        />
      )}
    </div>
  );
}


function BlockCard({ tpl, onEdit, onDelete }) {
  // Strip HTML to show a short plaintext snippet
  const plain = (tpl.html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\$\{[^}]+\}/g, (m) => m)
    .replace(/\s+/g, " ")
    .trim();
  const snippet = plain.length > 160 ? plain.slice(0, 160) + "…" : plain;

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-4" data-testid={`textblock-card-${tpl.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-[14.5px] text-zinc-50 truncate">{tpl.name}</h3>
            {!tpl.is_active && (
              <span className="text-[10px] uppercase font-bold text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">inaktiv</span>
            )}
            <span className="text-[10px] font-mono text-zinc-500">v{tpl.version}</span>
          </div>
          {tpl.description && (
            <p className="text-[12px] text-zinc-400 mt-0.5">{tpl.description}</p>
          )}
          {snippet && (
            <p className="text-[12px] text-zinc-500 mt-2 font-mono italic line-clamp-2">
              {snippet}
            </p>
          )}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button
            onClick={onEdit}
            data-testid={`textblock-edit-${tpl.id}`}
            className="h-8 w-8 rounded-md bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center transition"
            title="Bearbeiten"
          >
            <Edit3 size={14} className="text-zinc-300" />
          </button>
          <button
            onClick={onDelete}
            data-testid={`textblock-delete-${tpl.id}`}
            className="h-8 w-8 rounded-md bg-rose-500/15 hover:bg-rose-500/25 inline-flex items-center justify-center transition"
            title="Löschen"
          >
            <Trash2 size={14} className="text-rose-300" />
          </button>
        </div>
      </div>
    </div>
  );
}


function EditorModal({ mode, draft, setDraft, onClose, onSave, onPreview, onPdf, saving, previewing, previewHtml }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="textblock-editor-modal"
        className="bg-[#09090B] border border-zinc-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-[#09090B] border-b border-zinc-800 px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-bold text-[16px]">
            {mode === "new" ? "Neuer Textbaustein" : "Textbaustein bearbeiten"}
          </h2>
          <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-zinc-800 inline-flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase font-bold text-zinc-400 mb-1 tracking-wider">Name *</label>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                data-testid="textblock-name-input"
                placeholder="z.B. Zahlungsinfo AZ1"
                className="h-10 w-full px-3 bg-[#18181B] border border-zinc-800 rounded-lg text-[13px] focus:border-[#00E575] outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase font-bold text-zinc-400 mb-1 tracking-wider">Kategorie</label>
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                data-testid="textblock-category-select"
                className="h-10 w-full px-3 bg-[#18181B] border border-zinc-800 rounded-lg text-[13px]"
              >
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase font-bold text-zinc-400 mb-1 tracking-wider">Beschreibung</label>
            <input
              type="text"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              data-testid="textblock-description-input"
              placeholder="Kurze Beschreibung für dein Team …"
              className="h-10 w-full px-3 bg-[#18181B] border border-zinc-800 rounded-lg text-[13px] focus:border-[#00E575] outline-none"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase font-bold text-zinc-400 mb-1 tracking-wider">Inhalt</label>
            <TextBlockEditor
              value={draft.html}
              onChange={(html) => setDraft({ ...draft, html })}
            />
          </div>

          {previewHtml && (
            <div className="bg-[#18181B] border border-[#00E575]/40 rounded-xl p-4" data-testid="textblock-preview">
              <div className="text-[11px] uppercase font-bold text-[#00E575] mb-2 tracking-wider inline-flex items-center gap-1">
                <Eye size={11} /> Preview (Beispiel-Kontext)
              </div>
              <div
                className="prose prose-invert prose-sm max-w-none text-[13.5px] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          )}

          <label className="inline-flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              data-testid="textblock-active-toggle"
              className="h-4 w-4 accent-[#00E575]"
            />
            Aktiv (für Angebote, Rechnungen etc. verwendbar)
          </label>
        </div>

        <div className="sticky bottom-0 bg-[#09090B] border-t border-zinc-800 px-5 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <button
              onClick={onPreview}
              disabled={previewing || !draft.html}
              data-testid="textblock-preview-btn"
              className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-[13px] font-semibold inline-flex items-center gap-1.5"
            >
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              Preview
            </button>
            {mode === "edit" && (
              <button
                onClick={onPdf}
                data-testid="textblock-pdf-btn"
                className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[13px] font-semibold inline-flex items-center gap-1.5"
              >
                <FileText size={14} /> Als PDF
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[13px] font-semibold"
            >
              Abbrechen
            </button>
            <button
              onClick={onSave}
              disabled={saving || !draft.name.trim()}
              data-testid="textblock-save-btn"
              className="h-10 px-4 rounded-lg bg-[#00E575] text-[#09090B] hover:bg-[#00C964] disabled:opacity-40 text-[13px] font-bold inline-flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
